import UserStore from 'stores/UserStore';
import { formatMessageDate } from 'utils/date/formatUnixDate';

import { NAME } from './selectors';


export const API_STATES = {
  ERROR: 'ERROR',
  INITIAL: 'INITIAL',
  LOADING: 'LOADING',
  SUCCESS: 'SUCCESS',
};

export const API_STATES_VALUES = [
  'ERROR',
  'INITIAL',
  'LOADING',
  'SUCCESS',
];

export function getActiveConversationEmptyStructure() {
  return {
    canLoadMore: false,
    deleteMsgId: 0,
    isMassAdminThread: false,
    lastMsgId: 0,
    lastReadMsgId: 0,
    massAdminSenderId: 0,
    messages: [],
    originalLoadedMessages: [],
    receiver: {},
    threadId: 0,
    userId: 0,
    users: {},
    usersProperties: [],
  };
}

export function getActiveConversationPreloadDetails(previews, threadId) {
  const { user: { username } } = UserStore;

  const currPreview = previews.find((preview) => preview.threadId === threadId);

  const receiverId = Object.keys(currPreview.users).find(
    (KEY) => currPreview.users[KEY] !== username
  );
  const receiverName = currPreview.users[receiverId];

  const activeConversationPrealoadDetails = {
    userId: receiverId,
    name: receiverName,
    isFriend: false,
    isFavorite: false,
    isFan: false,
  };

  return activeConversationPrealoadDetails;
}

export const getMessagePostParams = ({ message, threadId }) => {
  const { user: { userId, username } } = UserStore;

  const currentTimestamp = Date.now();
  const params = {
    id: currentTimestamp,
    message,
    msgId: currentTimestamp,
    mType: 'text',
    senderId: userId,
    senderUsername: username,
    sendDate: formatMessageDate(currentTimestamp),
    threadId,
  };

  return params;
};

export const getMediaMessage = ({ mType, accessUrl, threadId }) => {
  const { user: { userId, username } } = UserStore;

  const timestamp = Date.now();

  return {
    id: 'newUploadMedia',
    message: accessUrl,
    msgId: timestamp,
    mType,
    progress: null,
    senderUsername: username,
    sendDate: formatMessageDate(timestamp),
    senderId: userId,
    threadId,
  };
};

export const getConversationReceiver = (conversation) => {
  const { user: { username } } = UserStore;

  const receiver = conversation.usersProperties.find(
    (u) => u.name !== username
  );

  return receiver;
};

export const getPreviewFromConversation = (messageFromFirebase, conversation) => {
  const receiver = getConversationReceiver(conversation);

  const preview = {
    isMassAdminThread: conversation.isMassAdminThread,
    lastMessageReadId: conversation.lastReadMsgId,
    lastPerformerMassMessageReadId: 0,
    preview: {
      ...messageFromFirebase,
      isHtml: false,
      isPerformerMassMessage: false,
    },
    read: false,
    receiverName: receiver.name,
    threadId: messageFromFirebase.threadId,
    unreadMsgCount: 1,
    users: { ...conversation.users },
  };

  return preview;
};

export const createContactGroups = (data) => {
  const groups = {};

  data
    .sort((a, b) => {
      const first = a.username.toUpperCase();
      const second = b.username.toUpperCase();
      if (first < second) {
        return -1;
      }
      if (first > second) {
        return 1;
      }

      return 0;
    })
    .forEach((contact) => {
      const { username } = contact;

      const firstLetter = username.charAt(0).toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(contact);
    });

  return groups;
};

export function formatBytes(bytes, decimals = 2) {
  if (!+bytes) {
    return '0 MB';
  }

  const k = 1024;
  const dm = (decimals < 0) ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export const IMAGE_MAX_SIZE_BYTES = 5200000;

export const VIDEO_MAX_SIZE_BYTES = 42000000;

export const IMAGE_MAX_SIZE = formatBytes(IMAGE_MAX_SIZE_BYTES);

export const VIDEO_MAX_SIZE = formatBytes(VIDEO_MAX_SIZE_BYTES);

export const IMAGE_SUPPORTED_FORMATS = [
  'image/vnd.wap.wbmp',
  'image/png',
  'image/x-png',
  'image/jpeg',
  'image/bmp',
  'image/gif',
];

export const VIDEO_SUPPORTED_FORMATS = [
  'video/x-flv',
  'video/mp4',
  'video/avi',
  'video/3gpp',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-ms-wmv',
  'video/webm',
  'video/x-matroska',
];

export const validateFile = (file) => {
  const { type, size, name } = file;

  const mType = type.substring(0, type.indexOf('/'));
  const previewSrc = URL.createObjectURL(file);

  let maxSize = 0;
  let supportedFormats = [];
  let unsupportedType = false;
  switch (mType) {
    case 'image':
      maxSize = IMAGE_MAX_SIZE_BYTES;
      supportedFormats = IMAGE_SUPPORTED_FORMATS;
      break;

    case 'video':
      maxSize = VIDEO_MAX_SIZE_BYTES;
      supportedFormats = VIDEO_SUPPORTED_FORMATS;
      break;

    default:
      unsupportedType = true;
  }

  const exceedsMaxSize = size >= maxSize;
  const unsupportedFormat = !supportedFormats.includes(type);
  const extensionMatch = name.match(/[^.]*$/);
  const extension = extensionMatch[0] || '';

  const output = {
    extension,
    format: mType,
    formatError: unsupportedFormat,
    name: name.replace(/[^.]*$/, ''),
    previewSrc,
    serverError: false,
    size: formatBytes(size),
    sizeError: exceedsMaxSize,
    type,
    typeError: unsupportedType,
  };

  return output;
};

export function isSameConversationOpen(getState, threadId) {
  const activeThreadId = getState()[NAME].getIn(['activeConversation', 'threadId']);

  return activeThreadId === threadId;
}
